import { useEffect, useState } from "react";
import { ChatModelMenuButton } from "./ChatModelSelector";
import { useAgentSessionContext } from "./AgentSessionContext";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { Icon } from "@/components/common/Icon";
import { listChatModels, postAgentJobRetry, type ChatModelOption } from "@/lib/api";
import type { ProviderOutageRenderBlock } from "@/lib/chat-render";
import { dedupeChatModels, resolveEffortForModel } from "@/lib/chat-models";
import { DEFAULT_CHAT_EFFORT, useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

interface ProviderOutageCardProps {
	block: ProviderOutageRenderBlock;
	canRetry: boolean;
}

/** モデルプロバイダーの一時障害を説明し、最新の失敗ターンだけ再試行できる警告カード。 */
export function ProviderOutageCard({ block, canRetry }: ProviderOutageCardProps) {
	const selectedModel = useChatStore((state) => state.selectedModel);
	const selectedEffort = useChatStore((state) => state.selectedEffort);
	const setSelectedModel = useChatStore((state) => state.setSelectedModel);
	const setSelectedEffort = useChatStore((state) => state.setSelectedEffort);
	const [models, setModels] = useState<ChatModelOption[]>([]);
	const [modelsLoadFailed, setModelsLoadFailed] = useState(false);
	const [retrying, setRetrying] = useState(false);
	const { startStreaming } = useAgentSessionContext();

	useEffect(() => {
		if (!canRetry) return;
		let cancelled = false;
		listChatModels()
			.then((availableModels) => {
				if (cancelled) return;
				setModels(dedupeChatModels(availableModels));
				setModelsLoadFailed(false);
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				console.error("再試行用モデル一覧の取得に失敗しました", error);
				setModelsLoadFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [canRetry]);

	const selectModel = (model: ChatModelOption) => {
		setSelectedModel(model.id);
		setSelectedEffort(resolveEffortForModel(model, selectedEffort, DEFAULT_CHAT_EFFORT));
	};

	const retry = async () => {
		const initial = useChatStore.getState();
		const chatId = initial.chatId;
		if (!canRetry || retrying || chatId === null) return;

		setRetrying(true);
		try {
			const result = await postAgentJobRetry(chatId, {
				model: initial.selectedModel,
				...(initial.selectedEffort === null ? {} : { effort: initial.selectedEffort }),
				...(Object.keys(initial.subagentModels).length === 0
					? {}
					: { subagentModels: initial.subagentModels }),
			});

			const current = useChatStore.getState();
			if (current.chatId !== chatId) return;
			current.setActiveAgentJob(result.jobId);
			current.setStatus("streaming");
			void startStreaming(chatId);
		} catch (error: unknown) {
			console.error("失敗ターンの再試行に失敗しました", error);
			useToastStore.getState().show({
				type: "error",
				message: error instanceof Error ? error.message : "処理の再試行に失敗しました。",
			});
		} finally {
			setRetrying(false);
		}
	};

	return (
		<div data-testid="provider-outage-card" style={{ padding: "8px 0" }}>
			<div
				style={{
					borderRadius: "var(--radius-4)",
					border: "1px solid var(--warning-line)",
					background: "var(--warning-subtle)",
					padding: "12px",
					fontSize: "var(--text-ui)",
					lineHeight: "var(--leading-body)",
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--warning)" }}>
					<Icon name="alertTriangle" size={16} color="var(--warning)" />
					<strong>サービスが混雑しています</strong>
				</div>
				{block.attemptCount > 1 && (
					<p style={{ margin: "6px 0 0 24px", color: "var(--fg-2)" }}>
						{block.attemptCount}回試しましたが、まだ混雑しています。
					</p>
				)}
				<p
					className="whitespace-pre-wrap wrap-anywhere"
					style={{ margin: "6px 0 0 24px", color: "var(--fg-2)" }}
				>
					{block.text}
				</p>
				{canRetry && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							gap: 8,
							marginTop: 10,
						}}
					>
						<ConfirmButton
							label="もう一度試す"
							loading={retrying}
							size="sm"
							onClick={() => void retry()}
							data-testid="provider-outage-retry"
						/>
						<ChatModelMenuButton
							models={models}
							value={selectedModel}
							onChange={selectModel}
							disabled={retrying || models.length === 0}
							tooltip={modelsLoadFailed ? "モデル一覧を取得できませんでした。" : undefined}
							testId="provider-outage-model-selector"
							menuTestId="provider-outage-model-selector-menu"
							itemTestIdPrefix="provider-outage-model-selector-item"
							appearance="secondary"
						/>
					</div>
				)}
			</div>
		</div>
	);
}
